import { Button } from '@design-system/button';
import { Typography } from '@design-system/typography';
import {
  isErrorResult,
  UnformattedUserAttributes,
} from '@phx-husky/auth-service';
import { Field, FieldType as FieldTypeEnum } from '@phx-husky/field';
import { CloseIcon } from '@phx-husky/icons';
import { Modal } from '@phx-husky/modal';
import { useForm } from '@phx-husky/use-form';
import { useProfile } from '@phx-husky/use-profile';
import { useTranslations } from '@phx-husky/use-translations';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';

import { DeliveryAddressWithId, EditAddressFormProps } from '../../models';
import * as styles from './edit-address-form.module.scss';

export const EditAddressForm = ({
  fields,
  title,
  mode,
  additionalAddresses,
  onClose,
}: EditAddressFormProps) => {
  const { setUserAttributes, validate, createFormattedAddressAttributes } =
    useProfile();
  const { translate } = useTranslations();
  const [error, setError] = useState('');
  const isDeliveryMode = mode === 'delivery';
  const close = (shouldUpdateProfile = false) => {
    onClose({ shouldUpdateProfile });
  };

  const submitAddressForm = async ({
    ...params
  }: UnformattedUserAttributes) => {
    const formattedAddress = createFormattedAddressAttributes(
      params
    ) as DeliveryAddressWithId;

    const attributes = {
      deliveryAddresses: (
        [
          ...(additionalAddresses || []),
          formattedAddress,
        ] as DeliveryAddressWithId[]
      )
        // remove `id` fields -> they are created in the browser
        .map(({ id: _id, ...address }) => address),
    };

    setError('');
    const result = await setUserAttributes(attributes);
    if (isErrorResult(result)) {
      setError(result.message ?? translate('defaultErrorMessage'));
    } else {
      close(true);
    }
  };

  const submitForm = async (params: UnformattedUserAttributes) => {
    setError('');
    const result = await setUserAttributes(params);
    if (isErrorResult(result)) {
      setError(result.message ?? translate('defaultErrorMessage'));
    } else {
      close(true);
    }
  };

  const fieldStatesInit = useMemo(
    () =>
      Object.keys(fields).reduce(
        (prev, curr) => ({
          ...prev,
          [curr]: fields[curr].defaultValue || '',
        }),
        {}
      ),
    [fields]
  );

  const { values, errors, handleChange, handleSubmit } = useForm({
    initialValues: fieldStatesInit,
    callback: isDeliveryMode ? submitAddressForm : submitForm,
    validate,
    validateOnChange: true,
  });

  return (
    <Modal isShowing close={close} disableOnClickClose>
      <Button
        className={classNames(
          styles.closeButton,
          'auth-profile-edit-form__close-button'
        )}
        onClick={() => close(false)}
        type="button"
      >
        <CloseIcon />
      </Button>

      <div
        className={classNames(
          styles.formWrapper,
          'auth-profile-edit-form__form-wrapper'
        )}
      >
        <Typography
          variant="body"
          tag="span"
          size="xl"
          weight="bold"
          align="start"
          className={classNames(styles.title, 'auth-profile-edit-form__title')}
          content={translate(`${title}EditTitle`)}
        />
        <form
          onSubmit={handleSubmit}
          className={classNames(styles.form, 'auth-profile-edit-form__form')}
        >
          {Object.entries(fields)
            .filter(([, { showInEdit }]) => showInEdit)
            .map(([name, { label, type, defaultValue, options }]) => (
              <Field
                key={name}
                label={label as string}
                errorMessage={errors.validation?.[name].message}
                name={name}
                type={FieldTypeEnum[type]}
                value={values[name]}
                onChange={handleChange}
                defaultValue={defaultValue as string}
                options={options}
                placeholder={label as string}
                className={classNames(
                  styles.formItem,
                  'auth-profile-edit-form__form-item'
                )}
              />
            ))}
          {error.length ? (
            <p className="field__error-message" data-testid="textField-error">
              {error}
            </p>
          ) : null}
          <div
            className={classNames(
              styles.buttonWrapper,
              'auth-profile-edit-form__button-wrapper'
            )}
          >
            <Button
              variant="primary"
              className={styles.button}
              id="personal-submit"
              type="submit"
              data-translate-key={`${title}EditSubmitButton`}
            >
              {translate(`${title}EditSubmitButton`)}
            </Button>
            <Button
              variant="secondary"
              className={styles.button}
              id="personal-cancel"
              type="button"
              data-translate-key={`${title}EditCancelButton`}
              onClick={() => close(false)}
            >
              {translate(`${title}EditCancelButton`)}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
